/* eslint-disable react-hooks/exhaustive-deps */
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getSortingOptions } from '../../../../../../common/global/helpers';
import { SortOption } from '../../../../../../common/global/models';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import { GridPager } from '../../../../../../common/widgets/GridPager';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../config';
import { DiscoverCardTypes } from '../../../../enums';
import { parseProducts } from '../../../../helpers';
import { ProductCardModel, ProductListModel } from '../../../../models/Product';
import ProductListItem from '../../../Common/ProductListItem';
import { getSortedProductCards, updatePagingQueryParam } from '../../utils';
import { SortValue } from './common';
import { getDefaultSortValue, getInitialSortValue } from './utils';
import { getQueryParams } from '../../../ProductsPage/utils';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { resources } from '../../../../resources/resources';

type PageContent = {
  total: number;
  items: ProductCardModel[] | undefined;
};

interface ProductListPanelProps {
  productList?: ProductListModel[];
  isDiscountVoucherList: boolean;
  isBenefitBucksList: boolean;
  showBrands?: boolean;
  handleSortingOrderChanged: (id: number) => void;
  isSearchResults?: boolean;
  initialSortId?: number;
  initialSkip?: number;
  initialTake?: number;
  forcedCategoryId?: string;
}

export default function ProductListPanel(
  props: Readonly<ProductListPanelProps>,
) {
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const {
    productList,
    isDiscountVoucherList,
    isBenefitBucksList,
    showBrands,
    handleSortingOrderChanged,
    isSearchResults,
    initialSortId,
    initialSkip,
    initialTake,
    forcedCategoryId,
  } = props;

  const params = useParams<{ categoryId?: string }>();
  const categoryId = forcedCategoryId || params.categoryId;
  const searchTerm = getQueryParams()?.searchTerm;
  const [sortedProductList, setSortedProductList] = useState<
    ProductCardModel[] | undefined
  >(undefined);
  const [sortValue, setSortValue] = useState<SortValue>(
    getInitialSortValue(
      translate,
      isDiscountVoucherList,
      isBenefitBucksList,
      isSearchResults ?? false,
      initialSortId,
    ),
  );
  const [page, setPage] = useState({
    skip: initialSkip ?? 0,
    take: initialTake ?? config.listPageSize,
  });
  const [pageContent, setPageContent] = useState<PageContent>({
    total: 0,
    items: undefined,
  });

  const sortingOptions = getSortingOptions(
    isDiscountVoucherList,
    isBenefitBucksList,
    isSearchResults ?? false,
  ).map((opt: SortOption) => {
    return {
      id: opt.id,
      text: translate(opt.resource),
    };
  });

  const handlePageChange = (e: any) => {
    updatePagingQueryParam(e.skip, e.take);
    setPage({
      skip: e.skip,
      take: e.take,
    });
    if (isSearchResults && globalThis.pa) {
      globalThis.pa.sendEvent('internal_search_result.display', {
        ise_keyword: searchTerm,
        ise_page: e.skip / e.take + 1,
        customer_group_id: customerInfo?.customerGroupId,
      });
    }
  };

  const handleSortingChanged = (event: DropDownListChangeEvent) => {
    handleSortingOrderChanged(event.value.id);
    setSortValue(
      sortingOptions.find(so => {
        return so.id === event.value.id;
      }) as SortValue,
    );
  };

  useEffect(() => {
    const sorted = getSortedProductCards[sortValue.id](sortedProductList);

    if (!isSearchResults && !_.isEqual(sorted, sortedProductList)) {
      setSortedProductList(sorted);
    }
  }, [sortedProductList]);

  useEffect(() => {
    if (productList) {
      const products = parseProducts(
        [...productList],
        showBrands && Number(categoryId) !== DiscoverCardTypes.Cashback,
      );
      setSortedProductList(products);
    } else {
      setSortedProductList(undefined);
    }
  }, [productList]);

  const resetPage = () => {
    updatePagingQueryParam(0, page.take);
    setPage({ skip: 0, take: page.take });
  };

  useEffect(() => {
    if (!sortedProductList) {
      return;
    }
    const items = sortedProductList.slice(page.skip, page.skip + page.take);
    setPageContent({
      total: sortedProductList.length,
      items,
    });
    if (page.skip > 0 && sortedProductList.length <= page.skip) {
      resetPage();
    }
  }, [page.skip, page.take, sortedProductList]);

  if (sortedProductList?.length === 0) {
    return (
      <div className="empty-state">
        <p>
          <output>{`0 ${translate(resources.searchPage.products)}`}</output>
        </p>
      </div>
    );
  }

  return (
    <Loader loading={!sortedProductList} loader={<LoadingSpinner />}>
      <div>
        <div className="panel panel-md">
          <div className="category-sort-pagination">
            <div className="category-sort">
              <Dropdown
                style={{ maxWidth: '300px' }}
                data={sortingOptions}
                textField="text"
                dataItemKey="id"
                defaultValue={getDefaultSortValue(
                  isSearchResults ?? false,
                  translate,
                )}
                onChange={handleSortingChanged}
                controlClassName="filter-dropdown category-sort-dropdown"
                popupClassName="filter-dropdown-popup category-sort-popup"
                value={sortValue}
              ></Dropdown>
            </div>
            <div className="category-pagination">
              <GridPager
                total={pageContent.total}
                skip={page.skip}
                take={page.take}
                buttonCount={5}
                previousNext
                size="medium"
                info={false}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="card-list card-list-4 card-list-sm-3 card-list-xs-2">
        {!!pageContent.items &&
          _.map(
            pageContent.items,
            (product: ProductCardModel, index: number) => {
              return (
                <div key={product.productId ?? product.brandId}>
                  <ProductListItem
                    product={product}
                    onProductClick={() => {
                      if (isSearchResults && globalThis.pa) {
                        globalThis.pa.sendEvent(
                          'internal_search_result.click',
                          {
                            ise_keyword: searchTerm,
                            ise_click_rank:
                              (index % (page.skip / page.take + 1)) + 1,
                            ise_page: page.skip / page.take + 1,
                            autocompletion: false,
                            customer_group_id: customerInfo?.customerGroupId,
                          },
                        );
                      }
                    }}
                  />
                </div>
              );
            },
          )}
      </div>
      <div className="minimal-pagination">
        <GridPager
          total={pageContent.total}
          skip={page.skip}
          take={page.take}
          buttonCount={5}
          previousNext
          size="small"
          info={false}
          onPageChange={handlePageChange}
        />
      </div>
    </Loader>
  );
}
